/**
 * 빌드 flavor 게이트 — dev/live 구분 SSoT.
 *
 * `__APP_FLAVOR__`는 vite.config의 `define`으로 빌드 시점에 문자열 치환된다
 * (build-apk.mjs가 `VITE_APP_FLAVOR=dev|live`를 web build 호출 시 주입).
 * 미주입(빈 문자열)이면 `import.meta.env.DEV`로 폴백 — vite dev 서버는 항상 dev로 판정한다.
 *
 * - `npm run dev:module-brick`       → DEV=true → "dev"
 * - `npm run apk:dev`  (VITE_APP_FLAVOR=dev)  → "dev"  (치트 노출)
 * - `npm run apk:live` (VITE_APP_FLAVOR=live) → "live" (치트 숨김)
 * - 그 외 prod 빌드(미주입)            → DEV=false → "live"
 */
declare const __APP_FLAVOR__: string;

/**
 * 앱 버전 SSoT — `apps/module-brick-idle/package.json`의 `version`.
 * vite.config의 `define`이 빌드 시점에 문자열 치환한다(package.json을 읽어 주입).
 * 미주입 시 "dev"로 폴백(vitest/jsdom 등 vite define 밖 환경).
 */
declare const __APP_VERSION__: string;
declare const __FORCE_PERF_OVERLAY__: string;
declare const __FORCE_RAF_LOOP__: string;
declare const __FORCE_TARGET_FPS__: string;
declare const __FORCE_FAST_RENDER__: string;
declare const __FORCE_LOW_SPEC_RENDER__: string;
declare const __FORCE_RENDER_RESOLUTION__: string;
declare const __FORCE_SMOOTH_STEP__: string;
declare const __PERF_BROWSER_TIMING__: string;
declare const __PERF_RENDERABLE_BUCKETS__: string;

/**
 * APP_VERSION 폴백 해석 — `__APP_VERSION__` define 주입 값을 받아 최종 버전 문자열을 반환.
 * vitest/jsdom 등 vite define 밖 환경에서 `injected`가 undefined 또는 빈 문자열일 때 `"dev"` 폴백.
 * @internal 단위테스트를 위해 export; 앱 진입은 `APP_VERSION` 상수를 사용.
 */
export function resolveAppVersion(injected: string | undefined): string {
  return injected != null && injected !== "" ? injected : "dev";
}

/** 현재 앱 버전(semver). 게임 설정화면 표시 + 빌드 산출물 버전과 동일 SSoT. */
export const APP_VERSION: string = resolveAppVersion(
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : undefined,
);

export type AppFlavor = "dev" | "live";

function resolveFlavor(): AppFlavor {
  const injected = typeof __APP_FLAVOR__ !== "undefined" ? __APP_FLAVOR__ : "";
  if (injected === "dev" || injected === "live") return injected;
  return import.meta.env.DEV ? "dev" : "live";
}

/** 현재 빌드 flavor. */
export const APP_FLAVOR: AppFlavor = resolveFlavor();

/** dev 치트 노출 게이트 — dev flavor에서만 true. LIVE 빌드는 치트 UI 전체를 숨긴다. */
export const CHEAT_ENABLED = APP_FLAVOR === "dev";

/** 진단 APK 전용 perf overlay 강제 활성화 플래그. 일반 빌드는 빈 문자열이라 false. */
export const FORCE_PERF_OVERLAY_ENABLED =
  typeof __FORCE_PERF_OVERLAY__ !== "undefined" && __FORCE_PERF_OVERLAY__ === "true";

/** 진단 APK 전용: Phaser FPS cap을 setTimeout 대신 RAF loop로 비교한다. */
export const FORCE_RAF_LOOP_ENABLED =
  typeof __FORCE_RAF_LOOP__ !== "undefined" && __FORCE_RAF_LOOP__ === "true";

function resolveForceTargetFps(): 15 | 30 | 60 | 120 | undefined {
  const raw = typeof __FORCE_TARGET_FPS__ !== "undefined" ? __FORCE_TARGET_FPS__ : "";
  const n = Number(raw);
  return n === 15 || n === 30 || n === 60 || n === 120 ? n : undefined;
}

/** 진단 APK 전용: 저장 설정과 무관하게 런타임 target FPS를 강제한다. */
export const FORCE_TARGET_FPS = resolveForceTargetFps();

/** 진단 APK 전용: WebView/GPU 병목 비교용 Phaser render fast-path를 활성화한다. */
export const FORCE_FAST_RENDER_ENABLED =
  typeof __FORCE_FAST_RENDER__ !== "undefined" && __FORCE_FAST_RENDER__ === "true";

/** 진단 APK 전용: 플레이 가능성 우선의 저사양 렌더 모드. 장식 FX/상시 redraw를 줄인다. */
export const FORCE_LOW_SPEC_RENDER_ENABLED =
  typeof __FORCE_LOW_SPEC_RENDER__ !== "undefined" && __FORCE_LOW_SPEC_RENDER__ === "true";

const forcedRenderResolution =
  typeof __FORCE_RENDER_RESOLUTION__ !== "undefined" && __FORCE_RENDER_RESOLUTION__ !== ""
    ? Number.parseFloat(__FORCE_RENDER_RESOLUTION__)
    : undefined;

export const FORCE_RENDER_RESOLUTION =
  forcedRenderResolution !== undefined && Number.isFinite(forcedRenderResolution) && forcedRenderResolution > 0
    ? forcedRenderResolution
    : undefined;

/** 진단 APK 전용: Phaser smoothStep을 강제로 켜거나 끈다. 미설정이면 Phaser 기본을 따른다. */
export const FORCE_SMOOTH_STEP =
  typeof __FORCE_SMOOTH_STEP__ !== "undefined" && __FORCE_SMOOTH_STEP__ !== ""
    ? __FORCE_SMOOTH_STEP__ === "true"
    : undefined;

/** 진단 APK 전용: WebView longtask/event-loop 계측. 켜면 로그 정보는 늘지만 perf 비용도 생긴다. */
export const PERF_BROWSER_TIMING_ENABLED =
  typeof __PERF_BROWSER_TIMING__ !== "undefined" && __PERF_BROWSER_TIMING__ === "true";

function resolveNonNegativeInt(injected: string | undefined, fallback: number): number {
  const n = Number(injected ?? "");
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
}

/** 진단 APK 전용: renderable type/name 상위 버킷 수. 0이면 bucket 집계 비용을 피한다. */
export const PERF_RENDERABLE_BUCKETS = resolveNonNegativeInt(
  typeof __PERF_RENDERABLE_BUCKETS__ !== "undefined" ? __PERF_RENDERABLE_BUCKETS__ : undefined,
  0,
);
